import { useI18n } from "vue-i18n";
import { useRoute, useRouter, useRuntimeConfig } from "nuxt/app";
import { getJobSlug } from "./useJob";

/**
 * Get region code
 *
 * @returns string region code (hu,ro)
 *
 */
export function getRegionCode() {
	return useRuntimeConfig().public.activeConfig.region.toLocaleLowerCase();
}

/**
 * Check region code
 *
 * @param code region code (hu,ro)
 * @returns boolean
 */
export function isRegion(code: string): boolean {
	return getRegionCode() == code.toLowerCase();
}

/**
 * Get the currently set language code
 *
 * @returns string language code (hu, ro)
 *
 */
export function getLangCode() {
	const { locale } = useI18n();
	return locale.value;
}

/**
 * Helper to construct a URL object for API requests, supporting relative paths in dev proxy.
 *
 * @param path The API endpoint path (including the leading slash)
 * @returns URL object
 */
export function constructApiUrl(path: string): URL {
	const apiUrl = globalConfig.API;
	if (apiUrl.startsWith("http")) {
		return new URL(apiUrl + path);
	} else {
		// Handle relative proxy path in dev
		const host = process.client ? window.location.origin : "http://localhost:3000";
		return new URL(apiUrl + path, host);
	}
}

/**
 * Create a error log for axios errors
 *
 * @param message string error message
 * @param error any Axios error object
 */
export function axiosErrorHandler(message: string, error: unknown) {
	if (error.response?.status == 404) {
		console.error(error.code + ": " + message, {
			status: error.response?.status || "no status",
			url: error.response?.config?.url || "no url",
			userAgent: error.response?.config?.headers["User-Agent"] || "no user agent",
		});
	} else {
		console.error(message, error);
	}
}

/**
 * Validates that a password meets all strength requirements:
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 *
 * @param password string
 * @returns boolean
 */
export function validatePassword(password: string): boolean {
	return (
		password.length >= 8 &&
		/[A-Z]/.test(password) &&
		/[a-z]/.test(password) &&
		/[0-9]/.test(password) &&
		/[^A-Za-z0-9]/.test(password)
	);
}

/**
 * Replace the slug in the URL with the language specific slug if it exists
 *
 * @param jobData job data object
 *
 */
export function replaceLangSpecificJobSlug(jobData: unknown, locale: string) {
	if (process.server) {
		return;
	}
	const route = useRoute();
	const router = useRouter();
	const localeSlug = getJobSlug(jobData as Parameters<typeof getJobSlug>[0], locale);
	const currentSlug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;

	if (!localeSlug || !currentSlug || localeSlug === currentSlug) {
		return;
	}

	const segments = route.path.split("/");
	const slugIndex = segments.lastIndexOf(String(currentSlug));
	if (slugIndex === -1) {
		return;
	}

	segments[slugIndex] = localeSlug;
	router.replace({ path: segments.join("/"), query: route.query, hash: route.hash });
}
